/**
 * Draft Safety Scan comparison presentation.
 *
 * Must not make a stronger claim than signed_record_effectiveness_claim_allowed /
 * resolve_recheck_result would freeze. MISSING != ZERO. Percent language is
 * fail-closed to the same canonical predicate.
 */

export const DRAFT_COMPARISON_OUTCOMES = [
  'DECREASED',
  'INCREASED',
  'NO CHANGE',
  'INCONCLUSIVE',
  'NOT COMPARABLE',
] as const;

export type DraftComparisonOutcome = (typeof DRAFT_COMPARISON_OUTCOMES)[number];

export type DraftComparisonInput = {
  baselinePresent?: boolean | null;
  controlPresent?: boolean | null;
  recheckPresent?: boolean | null;
  windowsComparable?: boolean | null;
  comparisonClaimable?: boolean | null;
  denominatorTrusted?: boolean | null;
  coverageGatePassed?: boolean | null;
  reviewGatePassed?: boolean | null;
  effectivenessClaimAllowed?: boolean | null;
  baselineCount?: number | null;
  followupCount?: number | null;
  baselineRate?: number | null;
  followupRate?: number | null;
  percentReduction?: number | null;
};

export type DraftComparisonPresentation = {
  outcome: DraftComparisonOutcome;
  headline: string;
  explanation: string;
  showPercent: boolean;
  percentReduction: number | null;
  observedBaselineCount: number | null;
  observedFollowupCount: number | null;
};

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'boolean') return null;
  if (value == null || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function finiteNonnegative(value: unknown): boolean {
  const numeric = finiteNumber(value);
  return numeric != null && numeric >= 0;
}

function finitePositive(value: unknown): boolean {
  const numeric = finiteNumber(value);
  return numeric != null && numeric > 0;
}

/** Mirrors signed_record_effectiveness_claim_allowed (fail closed). */
export function draftEffectivenessClaimAllowed(input: DraftComparisonInput): boolean {
  const statusOk = input.comparisonClaimable === true || input.windowsComparable === true;
  return (
    statusOk
    && input.effectivenessClaimAllowed === true
    && input.coverageGatePassed === true
    && input.reviewGatePassed === true
    && input.windowsComparable === true
    && input.denominatorTrusted === true
    && finiteNonnegative(input.baselineCount)
    && finiteNonnegative(input.followupCount)
    && finitePositive(input.baselineRate ?? 1)
    && finiteNumber(input.followupRate) != null
  );
}

function directionalFromPercent(percent: number | null): DraftComparisonOutcome | null {
  if (percent == null || !Number.isFinite(percent)) return null;
  if (percent > 0) return 'DECREASED';
  if (percent < 0) return 'INCREASED';
  return 'NO CHANGE';
}

export function resolveDraftComparisonPresentation(
  input: DraftComparisonInput,
): DraftComparisonPresentation {
  const baselineCount = finiteNumber(input.baselineCount);
  const followupCount = finiteNumber(input.followupCount);
  const percent = finiteNumber(input.percentReduction);
  const comparable = input.windowsComparable === true
    && input.comparisonClaimable === true
    && input.denominatorTrusted === true;
  const claimAllowed = draftEffectivenessClaimAllowed(input);

  if (input.baselinePresent === false) {
    return {
      outcome: 'NOT COMPARABLE',
      headline: 'Not comparable',
      explanation: 'A clean comparable pre-Control baseline is not available. This Safety Scan does not make a before/after effectiveness claim.',
      showPercent: false,
      percentReduction: null,
      observedBaselineCount: baselineCount,
      observedFollowupCount: followupCount,
    };
  }

  if (input.recheckPresent === false) {
    return {
      outcome: 'NOT COMPARABLE',
      headline: 'Not comparable',
      explanation: 'Follow-up measurement is not yet part of this Safety Scan. Observed facts may still be shown.',
      showPercent: false,
      percentReduction: null,
      observedBaselineCount: baselineCount,
      observedFollowupCount: followupCount,
    };
  }

  if (!comparable) {
    return {
      outcome: 'NOT COMPARABLE',
      headline: 'Not comparable',
      explanation: 'Follow-up observations are available, but comparison requirements are not met. This Safety Scan does not make a before/after effectiveness claim.',
      showPercent: false,
      percentReduction: null,
      observedBaselineCount: baselineCount,
      observedFollowupCount: followupCount,
    };
  }

  if (!claimAllowed) {
    const directional = directionalFromPercent(percent);
    if (directional == null) {
      return {
        outcome: 'INCONCLUSIVE',
        headline: 'Inconclusive',
        explanation: 'Comparison is valid, but the evidence does not establish a directional change that can be claimed.',
        showPercent: false,
        percentReduction: null,
        observedBaselineCount: baselineCount,
        observedFollowupCount: followupCount,
      };
    }
    return {
      outcome: 'INCONCLUSIVE',
      headline: 'Inconclusive',
      explanation: 'Comparison is valid, but effectiveness language is not authorized for this Safety Scan.',
      showPercent: false,
      percentReduction: null,
      observedBaselineCount: baselineCount,
      observedFollowupCount: followupCount,
    };
  }

  const outcome = directionalFromPercent(percent) || 'INCONCLUSIVE';
  if (outcome === 'INCONCLUSIVE') {
    return {
      outcome,
      headline: 'Inconclusive',
      explanation: 'Comparison is valid, but the evidence does not establish directional change.',
      showPercent: false,
      percentReduction: null,
      observedBaselineCount: baselineCount,
      observedFollowupCount: followupCount,
    };
  }
  return {
    outcome,
    headline: outcome === 'DECREASED'
      ? 'Decreased'
      : outcome === 'INCREASED'
        ? 'Increased'
        : 'No change',
    explanation: outcome === 'DECREASED'
      ? 'Reviewed recurrence was lower in the comparable follow-up window.'
      : outcome === 'INCREASED'
        ? 'Reviewed recurrence was higher in the comparable follow-up window.'
        : 'No material change was recorded across the comparable windows.',
    showPercent: true,
    percentReduction: percent,
    observedBaselineCount: baselineCount,
    observedFollowupCount: followupCount,
  };
}
